import { useTranslation } from 'react-i18next'
import { SocialDocument } from '../../../../model/domain/social-document'
import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react'
import {
    Alert,
    AlertType,
    InputTextField,
    LoadingView,
    Modal,
    Role,
    TextareaField,
} from '@cuatroochenta/co-admin-react-library'
import { translate } from '../../../../../I18n/i18n'
import './SaveDocumentModal.scss'
import FileIcon from '../../../common/components/FileIcon/FileIcon'
import { ShareCenterOption } from '../../../../model/domain/share-option'
import { ShareOptionsUtils } from '../../../../../utils/map-utils'
import AdvancedShareWithCentersSelector from '../../../common/components/AdvancedShareWithCentersSelector/AdvancedShareWithCentersSelector'
import AdvancedShareWithCentersToolbar from '../../../common/components/AdvancedShareWithCentersToolbar/AdvancedShareWithCentersToolbar'
import { User } from '../../../../model/domain/user'

interface SaveDocumentModalProps {
    user?: User
    show: boolean
    loading: boolean
    socialDocument: SocialDocument | null
    shareOptions?: ShareCenterOption[]
    loadingShareOptions: boolean
    onViewFile: () => void
    onDownloadFile: () => void
    onConfirm: (socialDocument: SocialDocument) => void
    onCancel: () => void
}

interface SaveDocumentModalState {
    title: string
    description: string
    file?: File
    publishingDate: string
    error: string
    showShareSelector: boolean
    shareOptions?: ShareCenterOption[]
    selectedNames: string[]
}

function SaveDocumentModal({
    user,
    show,
    loading,
    socialDocument,
    shareOptions: initialShareOptions,
    loadingShareOptions,
    onViewFile,
    onDownloadFile,
    onConfirm,
    onCancel,
}: SaveDocumentModalProps) {
    const { t } = useTranslation()
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [form, setForm] = useState<SaveDocumentModalState>({
        title: '',
        description: '',
        publishingDate: '',
        error: '',
        showShareSelector: false,
        shareOptions: initialShareOptions,
        selectedNames: [],
    })

    const {
        title,
        description,
        file,
        publishingDate,
        error,
        showShareSelector,
        selectedNames,
        shareOptions,
    } = form

    useEffect(() => {
        if (socialDocument) {
            handleFormChange({
                title: socialDocument.title ?? title,
                description: socialDocument.description ?? description,
                publishingDate: socialDocument.publishingDate ?? publishingDate,
                shareOptions: socialDocument.shareOptions,
            })
        } else {
            resetForm()
        }
    }, [socialDocument])

    useEffect(() => {
        if (!form.shareOptions || form.shareOptions.length === 0) {
            handleFormChange({ shareOptions: initialShareOptions })
        }
    }, [initialShareOptions, form.shareOptions])

    useEffect(() => {
        const selectedNames = ShareOptionsUtils.getSelectedNames(shareOptions)
        handleFormChange({ selectedNames })
    }, [shareOptions])

    const hasEditPermission = useMemo(() => {
        return (
            !socialDocument ||
            user?.data.roles.includes(Role.SUPERADMIN) ||
            user?.data.id === socialDocument?.userId
        )
    }, [user, socialDocument])

    const handleFormChange = (changes: Partial<SaveDocumentModalState>) => {
        setForm((form) => ({
            ...form,
            ...changes,
        }))
    }

    const handleFileButtonClick = () => {
        if (hasEditPermission) {
            fileInputRef.current?.click()
        }
    }

    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const file = e.target.files[0]

            if (file.size > 500_000_000) {
                return handleFormChange({
                    error: translate(t, 'TR_SAVE_MODAL_ERROR_FILE_SIZE'),
                })
            }

            handleFormChange({ file })
        }
    }

    const handleOpenCenterSelector = () => {
        handleFormChange({ showShareSelector: true })
    }

    const handleCancelCenterSelection = () => {
        handleFormChange({
            selectedNames: [],
            showShareSelector: false,
            shareOptions: shareOptions?.map((center) =>
                ShareOptionsUtils.copyCenterRecursive(center, {
                    checked: false,
                })
            ),
        })
    }

    const handleAcceptCenterSelection = () => {
        handleFormChange({ showShareSelector: false })
    }

    const handleCenterSelection = (selection?: ShareCenterOption[]) => {
        handleFormChange({ shareOptions: selection })
    }

    const resetForm = () => {
        setForm({
            title: '',
            description: '',
            file: undefined,
            publishingDate: '',
            error: '',
            showShareSelector: false,
            selectedNames: [],
        })
    }

    const handleViewFile = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation()
        onViewFile()
    }

    const handleDownloadFile = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation()
        onDownloadFile()
    }

    const handleCancel = () => {
        resetForm()
        onCancel()
    }

    const handleSubmit = () => {
        if (!socialDocument && !file) {
            return handleFormChange({
                error: 'socialDocuments.TR_SAVE_MODAL_ERROR_EMPTY_FIELDS',
            })
        }

        // if (publishingDate) {
        //     const parsedPublishingDate = new Date(publishingDate)
        //     if (parsedPublishingDate < new Date()) {
        //         return handleFormChange({
        //             error: translate(t, 'socialDocuments.TR_SAVE_MODAL_ERROR_PUBLISHING_DATE'),
        //         })
        //     }
        // }

        const newSocialDocument: SocialDocument = {
            id: socialDocument?.id,
            title: title ?? undefined,
            description: description ?? undefined,
            publishingDate: publishingDate ?? undefined,
            file,
            filename: file?.name,
            shareOptions,
        }

        resetForm()
        onConfirm(newSocialDocument)
    }

    const renderCenterSelector = () => (
        <AdvancedShareWithCentersSelector
            loading={loadingShareOptions}
            shareOptions={shareOptions}
            onCancel={handleCancelCenterSelection}
            onAccept={handleAcceptCenterSelection}
            onSelection={handleCenterSelection}
        />
    )

    const renderForm = () => (
        <div className="form-fields">
            <Alert
                show={!!error}
                type={AlertType.DANGER}
                message={translate(t, error)}
            />
            <InputTextField
                name="title"
                type="text"
                size={255}
                value={title}
                onChange={(value) => handleFormChange({ title: value })}
                disabled={!hasEditPermission}
                placeholder={translate(
                    t,
                    'socialDocuments.TR_SAVE_MODAL_TITLE_PLACEHOLDER'
                )}
            />
            <TextareaField
                name="description"
                placeholder={translate(
                    t,
                    'socialDocuments.TR_SAVE_MODAL_DESCRIPTION_PLACEHOLDER'
                )}
                value={description}
                onChange={(value) => handleFormChange({ description: value })}
                disabled={!hasEditPermission}
            />
            <div
                className="file-field"
                style={{ cursor: hasEditPermission ? 'pointer' : 'unset' }}
                onClick={handleFileButtonClick}
            >
                <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    accept="application/pdf"
                />
                <div className="field-row">
                    <FileIcon />
                    <div className="file-placeholder-texts">
                        <p>
                            {file?.name ??
                                socialDocument?.filename ??
                                translate(
                                    t,
                                    'socialDocuments.TR_SAVE_MODAL_FILE_PLACEHOLDER_TITLE'
                                )}
                        </p>
                        <span>
                            {translate(
                                t,
                                'socialDocuments.TR_SAVE_MODAL_FILE_PLACEHOLDER_SUBTITLE'
                            )}
                        </span>
                    </div>
                    {socialDocument?.filename ? (
                        <div className="file-actions">
                            <i className="fa fa-eye" onClick={handleViewFile} />
                            <i
                                className="fa fa-download"
                                onClick={handleDownloadFile}
                            />
                        </div>
                    ) : null}
                </div>
            </div>
            {/* <div className="form-row">
                <label htmlFor="publishingDate">
                    {translate(
                        t,
                        'socialDocuments.TR_SAVE_MODAL_PUBLISHING_DATE_LABEL'
                    )}
                </label>
                <DateField
                    name="publishingDate"
                    type="DATETIME"
                    value={publishingDate}
                    onChange={(value) =>
                        handleFormChange({ publishingDate: value })
                    }
                    placeholder={translate(
                        t,
                        'socialDocuments.TR_SAVE_MODAL_PUBLISHING_DATE_IMMEDIATELY'
                    )}
                    minDate={new Date()}
                />
            </div> */}
            {hasEditPermission ? (
                <AdvancedShareWithCentersToolbar
                    selectedOptions={selectedNames.join(', ')}
                    onClick={handleOpenCenterSelector}
                />
            ) : null}
        </div>
    )

    return (
        <Modal
            show={show}
            title={
                loading
                    ? ''
                    : translate(
                          t,
                          socialDocument
                              ? 'socialDocuments.TR_EDIT_MODAL_TITLE'
                              : 'socialDocuments.TR_ADD_MODAL_TITLE'
                      )
            }
            acceptText={translate(
                t,
                socialDocument
                    ? 'socialDocuments.TR_EDIT_MODAL_PUBLISH'
                    : 'socialDocuments.TR_ADD_MODAL_PUBLISH'
            )}
            acceptHandler={handleSubmit}
            disabledAcceptHandler={loading || !hasEditPermission}
            showHeader={!showShareSelector}
            showFooter={!showShareSelector}
            fitContentWidth={showShareSelector}
            onClose={handleCancel}
            children={
                loading ? (
                    <LoadingView loading />
                ) : showShareSelector ? (
                    renderCenterSelector()
                ) : (
                    renderForm()
                )
            }
        />
    )
}

export default SaveDocumentModal
